import { isNil } from 'lodash';
import { ReactNode, useContext, useEffect, useId, useMemo, useRef } from 'react';
import { getAnchorName } from '@shared/styles';
import { useBoolean } from '@shared/util/hooks';
import {
  MenuAlignment,
  MenuContext,
  MenuContextValue,
  ParentMenuContext,
  RootMenuContext,
} from './MenuContext';

export interface MenuProps {
  children?: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  isOpenByDefault?: boolean;
  anchorName?: string;
  align?: MenuAlignment;
  closeOnSelect?: boolean;
  lightDismiss?: boolean;
}

export function MenuRoot({
  children,
  isOpen: isOpenProp,
  onOpenChange,
  isOpenByDefault = false,
  anchorName: anchorNameProp,
  align = 'right',
  closeOnSelect = true,
  lightDismiss = true,
}: MenuProps) {
  const rawId = useId();
  const anchorName = anchorNameProp ?? getAnchorName('menu', rawId);

  const rootMenu = useContext(RootMenuContext);
  const parentMenu = useContext(MenuContext);

  const [isOpen, open, close, toggle, , setValue] = useBoolean(isOpenProp ?? false);

  // Open after mount when isOpenByDefault is true. Using a ref + effect (rather than
  // initializing useState with isOpenByDefault) ensures the parent popover's showPopover()
  // has already run before the child tries to open — otherwise the browser's auto-dismiss
  // logic closes the child when the parent popover subsequently calls showPopover().
  const isOpenByDefaultRef = useRef(isOpenByDefault);
  useEffect(() => {
    if (isOpenByDefaultRef.current) {
      open();
    }
  }, [open]);

  useEffect(() => {
    if (!isNil(isOpenProp)) {
      setValue(isOpenProp);
    }
  }, [isOpenProp, setValue]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const menu = useMemo<MenuContextValue>(
    () => ({ anchorName, isOpen, open, close, toggle, closeOnSelect, align, lightDismiss }),
    [anchorName, isOpen, open, close, toggle, closeOnSelect, align, lightDismiss],
  );

  return (
    <RootMenuContext.Provider value={rootMenu ?? menu}>
      <ParentMenuContext.Provider value={parentMenu}>
        <MenuContext.Provider value={menu}>{children}</MenuContext.Provider>
      </ParentMenuContext.Provider>
    </RootMenuContext.Provider>
  );
}

export function SubmenuRoot(props: MenuProps) {
  return <MenuRoot {...props} />;
}
